import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  clientMovementForms: [],
  currentClientMovementForm: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Client Movement Forms
export const fetchClientMovementForms = createAsyncThunk(
  'clientMovementForms/fetchClientMovementForms',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/client-movement-request-forms/${patientId}` : '/client-movement-request-forms';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch movement forms'
      );
    }
  }
);

// Fetch a single Client Movement Form
export const fetchClientMovementForm = createAsyncThunk(
  'clientMovementForms/fetchClientMovementForm',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/client-movement-request-forms/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch movement form'
      );
    }
  }
);

// Create a new Client Movement Form
export const createClientMovementForm = createAsyncThunk(
  'clientMovementForms/createClientMovementForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/client-movement-request-forms', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create movement form'
      );
    }
  }
);

// Update an existing Client Movement Form
export const updateClientMovementForm = createAsyncThunk(
  'clientMovementForms/updateClientMovementForm',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/client-movement-request-forms/${id}`, formData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update movement form'
      );
    }
  }
);

// Delete a Client Movement Form
export const deleteClientMovementForm = createAsyncThunk(
  'clientMovementForms/deleteClientMovementForm',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/client-movement-request-forms/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete movement form'
      );
    }
  }
);

// ==================== Slice ====================
const clientMovementFormSlice = createSlice({
  name: 'clientMovementForms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentClientMovementForm: (state, action) => {
      state.currentClientMovementForm = action.payload;
    },
    clearCurrentClientMovementForm: (state) => {
      state.currentClientMovementForm = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchClientMovementForms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClientMovementForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientMovementForms = action.payload;
      })
      .addCase(fetchClientMovementForms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchClientMovementForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClientMovementForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentClientMovementForm = action.payload;
      })
      .addCase(fetchClientMovementForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createClientMovementForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createClientMovementForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientMovementForms.unshift(action.payload);
      })
      .addCase(createClientMovementForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateClientMovementForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateClientMovementForm.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const index = state.clientMovementForms.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.clientMovementForms[index] = updated;
        }
        if (state.currentClientMovementForm?.id === updated.id) {
          state.currentClientMovementForm = updated;
        }
      })
      .addCase(updateClientMovementForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteClientMovementForm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteClientMovementForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientMovementForms = state.clientMovementForms.filter(
          (d) => d.id !== action.payload
        );
        if (state.currentClientMovementForm?.id === action.payload) {
          state.currentClientMovementForm = null;
        }
      })
      .addCase(deleteClientMovementForm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentClientMovementForm,
  clearCurrentClientMovementForm,
} = clientMovementFormSlice.actions;

export default clientMovementFormSlice.reducer;
